import { useAuth } from '@youversion/auth';
import { Box, Button, Text } from '@youversion/design-system-components';
import TriangleDownStroked from '@youversion/design-system-icons/lib/TriangleDownStroked';
import CurrencySelector from 'components/ui/dialogs/CurrencySelector';
import GivingHistoryDialog from 'components/ui/dialogs/GivingHistoryDialog';
import { AnimatePresence } from 'framer-motion';
import NextLink from 'next/link';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { firebaseLogEvent } from 'utils/firebase';
import { stringReplacer } from 'utils/helpers/stringReplacer';
import { useCurrency } from 'utils/store/currency';

import { YouversionWordmark } from './logo';
import { Profile } from './Profile/Profile';

interface Props {
  className?: string;
  leftElement?: React.ReactElement | null;
  onSignInClick?: () => void;
  rightElement?: React.ReactElement | null;
  showCurrencySelector?: boolean;
}

/**
 * Handles header content.
 *
 * @param className - Classes for the header container.
 * @param leftElement - Component to be shown on the left side of the header.
 * @param rightElement - Component to be shown on the right side of the header.
 * @param showCurrencySelector - Determines if currency selector should be shown.
 * @param onSignInClick - Callback for sign in button click.
 *
 * @example
 *
 * ```typescriptreact
 * <Header
 *   onSignInClick={handleClick}
 *   showCurrencySelector={true} />
 * ```
 */
export default function Header({
  className,
  onSignInClick = () => {},
  leftElement,
  rightElement,
  showCurrencySelector = true,
}: Props) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [currencySelectorOpen, setCurrencySelectorOpen] = useState(false);
  const [givingHistoryOpen, setGivingHistoryOpen] = useState(false);
  const { currencies, selectedCurrency } = useCurrency();
  const currencySelectorVisible = showCurrencySelector || (showCurrencySelector === undefined && currencies?.length);

  return (
    <>
      <header className={`${className} mbe-1 min-h-4 p-1.5 plb-3 md:min-h-3 md:p-2 md:plb-1`}>
        <Box className='flex flex-col md:flex-row items-center mbs-2'>
          {leftElement || (
            <Box className='w-full grid grid-cols-1 place-items-center md:place-items-start gap-0 mbe-2 md:mbe-0'>
              <NextLink className='no-underline hover:no-underline ' href='https://bible.com/bible'>
                <YouversionWordmark
                  className='text-gray-50 dark:text-white'
                  title={t('giving.titles.youversion_logo')}
                />
              </NextLink>
            </Box>
          )}
          {rightElement || (
            <Box className='flex items-center gap-2 mbs-1'>
              {currencySelectorVisible ? (
                <Button
                  icon={TriangleDownStroked}
                  iconPosition='end'
                  id='btn_currency_selector'
                  onClick={() => {
                    firebaseLogEvent('GivingButtonTap', { label: 'Currency', screen: 'giving_form' });
                    setCurrencySelectorOpen(true);
                  }}
                  size='xs'
                  title={t('giving.titles.select_currency')}
                  variant='stroked:contrast'
                >
                  {selectedCurrency}
                </Button>
              ) : null}
              {user ? (
                <>
                  <button onClick={() => setGivingHistoryOpen(true)}>
                    <Text
                      className='hover:text-gray-20 text-gray-50 hover:border-b-small border-gray-25 whitespace-nowrap'
                      size='md'
                      variant='label'
                    >
                      {t('giving.titles.giving_history')}
                    </Text>
                  </button>
                  <Profile
                    avatarUrl={
                      user?.avatarUrl ? stringReplacer(user?.avatarUrl, { height: '32', width: '32' }) : undefined
                    }
                    name={user?.firstName ? user?.firstName : user.username}
                  />
                </>
              ) : (
                <Box className='cursor-pointer' id='btn_sign_in' onClick={onSignInClick}>
                  <Text
                    className='mbe-0 hover:text-gray-20 text-text-light whitespace-nowrap hover:border-b-small border-gray-25 dark:text-text-dark'
                    size='md'
                    variant='label'
                  >
                    {t('giving.cta.sign_in')}
                  </Text>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </header>
      <AnimatePresence>
        {currencySelectorOpen ? (
          <CurrencySelector
            onClose={() => {
              setCurrencySelectorOpen(false);
            }}
          />
        ) : null}
      </AnimatePresence>
      <AnimatePresence>
        {givingHistoryOpen ? <GivingHistoryDialog onClose={() => setGivingHistoryOpen(false)} /> : null}
      </AnimatePresence>
    </>
  );
}
